import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { JwtHelperService } from '@auth0/angular-jwt';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { UserPermissions } from '../domain/security/user-permissions';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
   private jwtHelper = new JwtHelperService();

   private tokenLoadInFlight?: Promise<boolean>;

   constructor(private auth: AuthService) {}

   async getPermission(): Promise<UserPermissions> {
      await this.ensureTokenDataLoaded();

      const permissionsRaw = localStorage.getItem('permissions') ?? '';
      const rolesRaw = localStorage.getItem('roles') ?? '';

      const userPermissions = new UserPermissions();

      userPermissions.hasAudit = permissionsRaw.includes('read:audit-bucket');
      userPermissions.hasCreateAudit = permissionsRaw.includes('create:audit-bucket');
      userPermissions.hasWorkflow = permissionsRaw.includes('workflow:general');
      userPermissions.hasWorkflowRun = permissionsRaw.includes('workflow:launch');

      userPermissions.isPropAdmin =
         rolesRaw.includes('prop_admin') || rolesRaw.includes('account_owner');

      userPermissions.isBillingAdmin =
         rolesRaw.includes('billing_admin') || rolesRaw.includes('account_owner');

      userPermissions.updatePermissions(permissionsRaw);

      return userPermissions;
   }

   clearLocalCache(): void {
      localStorage.removeItem('permissions');
      localStorage.removeItem('roles');
      localStorage.removeItem('perm_cache_sub');
      localStorage.removeItem('lastRefreshTime');
      sessionStorage.removeItem('perm_loaded');
   }

   private async waitForAuthReady(): Promise<void> {
      await firstValueFrom(
         this.auth.isLoading$.pipe(
            filter((loading) => !loading),
            take(1)
         )
      );
   }

   private async ensureTokenDataLoaded(): Promise<void> {
      await this.waitForAuthReady();

      const loadedThisSession = sessionStorage.getItem('perm_loaded') === '1';
      const hasCachedValues =
         !!localStorage.getItem('permissions') || !!localStorage.getItem('roles');

      const user = await firstValueFrom(this.auth.user$);
      const sub = user?.sub ?? '';
      const cachedSub = localStorage.getItem('perm_cache_sub') ?? '';

      if (sub && cachedSub && cachedSub !== sub) {
         this.clearLocalCache();
      }
      if (sub) {
         localStorage.setItem('perm_cache_sub', sub);
      }

      if (loadedThisSession && hasCachedValues) return;

      if (!this.tokenLoadInFlight) {
         this.tokenLoadInFlight = this.loadTokenData().finally(() => {
            this.tokenLoadInFlight = undefined;
         });
      }

      const loaded = await this.tokenLoadInFlight;

      if (loaded) {
         sessionStorage.setItem('perm_loaded', '1');
      } else {
         sessionStorage.removeItem('perm_loaded');
      }
   }

   private async loadTokenData(): Promise<boolean> {
      const isAuthenticated = await firstValueFrom(this.auth.isAuthenticated$);

      if (!isAuthenticated) return false;

      let token: string | undefined;

      try {
         token = await firstValueFrom(
            this.auth.getAccessTokenSilently({
               authorizationParams: { audience: environment.auth.audience }
            })
         );
      } catch {
         return false;
      }

      if (!token) return false;

      const tokenJson: any = this.jwtHelper.decodeToken(token);
      if (!tokenJson) return false;

      const perms = tokenJson.permissions;
      if (Array.isArray(perms)) {
         localStorage.setItem('permissions', perms.join('|'));
      } else if (typeof perms === 'string') {
         localStorage.setItem('permissions', perms);
      } else {
         localStorage.removeItem('permissions');
      }

      const roles = tokenJson.tyallsRoles;
      if (Array.isArray(roles)) {
         localStorage.setItem('roles', roles.join('|'));
      } else if (typeof roles === 'string') {
         localStorage.setItem('roles', roles);
      } else {
         localStorage.removeItem('roles');
      }

      return true;
   }
}
